import { useSelector } from "react-redux";

import { UserDto } from "../../DTOs/User/UserDto";
import { RoleConfig } from "../../DTOs/Settings/RoleConfigDto";
import { RootState } from "../../state/store";

const heldNames = (user: UserDto): string[] => user.roles.map(r => r.name);

const isSuperAdmin = (userRoles: string[]): boolean =>
  userRoles.map(x => (x || "").toLowerCase().replace(/\s+/g, '')).includes("superadmin");

export const hasAnyRole = (user: UserDto | null, required: string | string[]): boolean => {
  if (!user) return false;

  const roles = Array.isArray(required) ? required : [required];
  const userRoles = heldNames(user);

  if (isSuperAdmin(userRoles)) return true;

  return roles.some(role => userRoles.includes(role));
};

export function hasAllRoles(user: UserDto | null, roles: string[]): boolean {
  if (!user) return false;

  const userRoles = heldNames(user);

  if (isSuperAdmin(userRoles)) return true;

  return roles.every(role => userRoles.includes(role));
}

export function hasRole(user: UserDto | null, role: string): boolean {
  if (!user) return false;
  return heldNames(user).includes(role);
}

export function hasArea(user: UserDto | null, areaKey: string): boolean {
  if (!user) return false;
  if (isSuperAdmin(heldNames(user))) return true;
  return user.areaKeys.includes(areaKey);
}

export function hasTile(user: UserDto | null, tileKey: string): boolean {
  if (!user) return false;
  if (isSuperAdmin(heldNames(user))) return true;
  return (user.visibleTileKeys ?? []).includes(tileKey);
}

export function isSuperZoneAdmin(user: UserDto | null, roleConfig: RoleConfig | null): boolean {
  if (!user || !roleConfig) return false;
  if (isSuperAdmin(heldNames(user))) return true;
  return hasRole(user, roleConfig.privilegeRoles.admin) && roleConfig.superZoneRoleNames.some(zone => hasRole(user, zone));
}

export function canEditModule(user: UserDto | null, module: string | undefined): boolean {
  if (!user || !module) return false;
  return user.editableModules.includes(module);
}

export function useIsViewOnly(modules: string | string[]): boolean {
  const user = useSelector((s: RootState) => s.authReducer.user);
  const list = Array.isArray(modules) ? modules : [modules];
  return !list.some(m => canEditModule(user, m));
}